import 'css/src/components/common/comments/VideoComment';

import React, { useCallback, useEffect, useRef } from 'react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';

import type { CommentablePersonProfile } from 'js/src/client';
import type { onBeforeCommentingT } from 'js/src/components/common/comments/CommentActionsPlugin';
import CommentActionsPlugin from 'js/src/components/common/comments/CommentActionsPlugin';
import FocusPlugin from 'js/src/components/common/comments/FocusPlugin';
import { MentionNode } from 'js/src/components/common/comments/MentionNode';
import MentionsPlugin from 'js/src/components/common/comments/MentionsPlugin';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';

const CONTENT_EDITABLE = <ContentEditable />;
const PLACEHOLDER = <div className="comment-placeholder pe-none">Leave a comment</div>;

type Props = {
    videoOwner: number | null;
    slideId: string;
    showCommentButtons: boolean;
    mentionableUsers: Immutable.List<ImmutableMap<CommentablePersonProfile>>;
    onBeforeCommenting?: onBeforeCommentingT;
    video_id: number | null;
    onCommentInputFocus?: () => void;
};
export const AddComment: React.FC<Props> = React.memo(function AddComment({
    slideId,
    showCommentButtons,
    mentionableUsers,
    videoOwner,
    onBeforeCommenting,
    video_id,
    onCommentInputFocus,
}) {
    const [isFocused, setIsFocused] = React.useState(false);
    const [people, setPeople] = React.useState<Immutable.List<
        ImmutableMap<CommentablePersonProfile>
    > | null>();

    useEffect(() => {
        let usersWithAccessToVideo = mentionableUsers;
        // filter out the commenter themselves from list of mentionables
        if (window.SERVER.USER) {
            // external users will not be able to mention, so we don't need condition for them
            usersWithAccessToVideo = usersWithAccessToVideo.filter(
                (user) => user.get('id') !== window.SERVER.USER.id,
            );
        }
        setPeople(usersWithAccessToVideo);
    }, [mentionableUsers]);

    const handleFocus = React.useCallback(() => {
        setIsFocused(true);
        typeof onCommentInputFocus === 'function' && onCommentInputFocus();
    }, [onCommentInputFocus]);

    const handleBlur = React.useCallback(() => {
        setIsFocused(false);
    }, []);

    const onLexicalError = useCallback((e: Error) => {
        ErrorReporter.capture(e);
    }, []);

    const mentionMenuContainerRef = useRef<HTMLDivElement | null>(null);

    if (videoOwner === null || video_id === null) {
        // loading
        return null;
    }

    return (
        <div className="add-comment mb-l5-16 mt-l5-8">
            <LexicalComposer
                initialConfig={{
                    editable: true,
                    namespace: 'add-comment',
                    onError: onLexicalError,
                    nodes: [MentionNode],
                }}
            >
                <div
                    className="add-comment_composer position-relative"
                    ref={mentionMenuContainerRef}
                >
                    <PlainTextPlugin
                        contentEditable={CONTENT_EDITABLE}
                        placeholder={PLACEHOLDER}
                        ErrorBoundary={LexicalErrorBoundary}
                    />

                    <ClearEditorPlugin />

                    {people && (
                        <MentionsPlugin
                            people={people}
                            menuContainerRef={mentionMenuContainerRef}
                        />
                    )}
                </div>

                <AutoFocusPlugin />

                <CommentActionsPlugin
                    showing={showCommentButtons || isFocused}
                    onBeforeCommenting={onBeforeCommenting}
                    videoOwner={videoOwner}
                    videoId={video_id}
                    slideId={slideId}
                />

                <FocusPlugin onFocus={handleFocus} onBlur={handleBlur} />
            </LexicalComposer>
        </div>
    );
});

export default AddComment;
